class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]",
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.isAgDesktopHeaderSearch = this.classList.contains("header__ag-search");
    this.isAgMobileHeaderSearch = this.classList.contains(
      "header__ag-mobile-predictive-search",
    );
    this.isAgHeaderSearch =
      this.isAgDesktopHeaderSearch || this.isAgMobileHeaderSearch;
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";
    this.resultsSearchTerm = "";
    this.pendingSubmitQuery = "";

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener("focus", this.onFocus.bind(this));
    // Listen on the input itself so Enter is cancelled before the browser can
    // perform the form's native submit action.
    this.input.addEventListener("keydown", this.onKeydown.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("click", this.onClick.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  hasCurrentResults() {
    return (
      this.getAttribute("results") === "true" &&
      this.resultsSearchTerm === this.getQuery()
    );
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    if (
      this.pendingSubmitQuery &&
      this.pendingSubmitQuery !== newSearchTerm
    ) {
      this.pendingSubmitQuery = "";
    }

    // A fast Enter can start the current query before the debounced input
    // handler runs. Do not abort and repeat that same request.
    if (newSearchTerm === this.searchTerm) return;

    this.abortPendingRequests();
    this.clearSelectedOption();

    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector(
        '[id^="predictive-search-results-groups-wrapper"]',
      )?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const query = this.getQuery();

    if (!query.length) {
      this.pendingSubmitQuery = "";
      event.preventDefault();
      return;
    }

    if (
      this.searchTerm !== query ||
      !this.hasCurrentResults() ||
      this.hasAttribute("loading")
    ) {
      event.preventDefault();
      this.pendingSubmitQuery = query;

      if (this.searchTerm !== query) this.onChange();
      else if (!this.hasAttribute("loading")) this.getSearchResults(query);
      return;
    }

    if (
      this.querySelector('[aria-selected="true"] a') ||
      this.querySelector(".predictive-search__empty-message")
    ) {
      this.pendingSubmitQuery = "";
      event.preventDefault();
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortPendingRequests();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.hasCurrentResults()) {
      this.open();
    } else if (!this.hasAttribute("loading")) {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (event.target !== this.input) return;
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.key === "Enter" && !event.isComposing) {
      const query = this.getQuery();

      // Always stop the input's native Enter submit. If an option is selected,
      // onKeyup activates it; otherwise submit only after results for this exact
      // query have finished loading and have been checked for an empty state.
      event.preventDefault();

      if (
        !query.length ||
        (this.hasCurrentResults() &&
          !this.hasAttribute("loading") &&
          this.querySelector(
            '[aria-selected="true"] a, button[aria-selected="true"]',
          ))
      ) {
        this.pendingSubmitQuery = "";
        return;
      }

      if (
        this.searchTerm === query &&
        this.hasCurrentResults() &&
        !this.hasAttribute("loading")
      ) {
        if (this.querySelector(".predictive-search__empty-message")) {
          this.pendingSubmitQuery = "";
          return;
        }

        this.pendingSubmitQuery = query;
        this.resolvePendingSubmit();
        return;
      }

      this.pendingSubmitQuery = query;
      if (this.searchTerm !== query) this.onChange();
      else if (!this.hasAttribute("loading")) this.getSearchResults(query);
      return;
    }

    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();
    }
  }

  onClick(event) {
    if (event.target.closest("[data-predictive-search-submit]")) {
      event.preventDefault();
      this.submitSearch();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]",
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open") || !this.hasCurrentResults()) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item"),
    ).filter((element) => element.offsetParent !== null);

    if (!allVisibleElements.length) return;

    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    if (!this.hasCurrentResults() || this.hasAttribute("loading")) return;

    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]',
    );

    if (selectedOption) selectedOption.click();
  }

  submitSearch() {
    if (!this.getQuery().length) return;

    if (this.input.form?.requestSubmit) {
      this.input.form.requestSubmit();
      return;
    }

    this.input.form?.submit();
  }

  abortPendingRequests() {
    this.abortController.abort();
    this.abortController = new AbortController();
  }

  getSearchResults(searchTerm) {
    const searchContext = this.isAgHeaderSearch ? "header" : "default";
    const queryKey = `${searchContext}-${searchTerm.toLowerCase()}`;
    const signal = this.abortController.signal;
    const searchParams = new URLSearchParams({
      q: searchTerm,
      section_id: "predictive-search",
    });

    if (this.isAgHeaderSearch) {
      searchParams.set("resources[type]", "product,article,page");
      searchParams.set("resources[limit]", "3");
      searchParams.set("resources[limit_scope]", "each");
    }

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey], searchTerm);
      return;
    }

    const predictiveSearchRequest = fetch(
      `${routes.predictive_search_url}?${searchParams.toString()}`,
      {
        signal,
      },
    ).then((response) => {
      if (!response.ok) {
        const error = new Error(response.status);
        this.close();
        throw error;
      }

      return response.text();
    });
    const headerSearchDataRequest = this.isAgHeaderSearch
      ? this.getHeaderSearchData(searchTerm)
      : Promise.resolve(null);

    Promise.all([predictiveSearchRequest, headerSearchDataRequest])
      .then(([text, headerSearchData]) => {
        if (signal.aborted || this.searchTerm !== searchTerm) return;

        const resultsSection = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search");

        if (!resultsSection) {
          throw new Error("Predictive search section was not found");
        }

        if (headerSearchData) {
          this.applyHeaderSearchData(resultsSection, headerSearchData);
        }

        const resultsMarkup = resultsSection.innerHTML;

        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            if (
              predictiveSearchInstance.isAgHeaderSearch ===
              this.isAgHeaderSearch
            ) {
              predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
            }
          },
        );
        this.renderSearchResults(resultsMarkup, searchTerm);
      })
      .catch((error) => {
        if (signal.aborted || error?.code === 20 || error?.name === "AbortError") {
          // Code 20 means the call was aborted
          return;
        }
        if (this.searchTerm !== searchTerm) return;
        this.close();
        throw error;
      });
  }

  getHeaderSearchData(searchTerm) {
    const searchParams = new URLSearchParams({
      q: searchTerm,
      "options[prefix]": "last",
      view: "count",
    });
    const searchUrl = new URL(this.input.form.action, window.location.origin);

    searchParams.forEach((value, key) => {
      searchUrl.searchParams.set(key, value);
    });

    return window.agSearchResults(searchUrl.toString(), this.abortController.signal)
      .catch((error) => {
        if (error?.code === 20 || error?.name === "AbortError") {
          throw error;
        }

        return null;
      });
  }

  applyHeaderSearchData(resultsSection, headerSearchData) {
    const groupsWrapper = resultsSection.querySelector(
      "#predictive-search-results-groups-wrapper",
    );
    const loadingState = resultsSection.querySelector(
      ".predictive-search__loading-state",
    );
    const footer = resultsSection.querySelector(
      "#predictive-search-option-search-keywords",
    );
    const liveRegionCount = resultsSection.querySelector(
      "[data-predictive-search-live-region-count-value]",
    );

    if (groupsWrapper) {
      groupsWrapper.outerHTML = headerSearchData.groupsMarkup;
    } else if (loadingState) {
      loadingState.insertAdjacentHTML(
        "beforebegin",
        headerSearchData.groupsMarkup,
      );
    }

    if (footer) {
      footer.outerHTML = headerSearchData.footerMarkup;
    } else if (headerSearchData.footerMarkup.trim() && loadingState) {
      loadingState.insertAdjacentHTML(
        "afterend",
        headerSearchData.footerMarkup,
      );
    }

    if (liveRegionCount && headerSearchData.liveRegionText) {
      liveRegionCount.textContent = headerSearchData.liveRegionText;
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.removeAttribute("aria-hidden");
    this.statusElement.textContent = statusText;
  }

  renderSearchResults(resultsMarkup, searchTerm) {
    this.clearSelectedOption();
    this.predictiveSearchResults.innerHTML = resultsMarkup;

    if (this.isAgHeaderSearch) {
      this.limitHeaderPredictiveResults();
    }

    this.scopeResultIds();
    this.resultsSearchTerm = searchTerm;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
    this.resolvePendingSubmit();
  }

  resolvePendingSubmit() {
    const query = this.getQuery();

    if (
      !this.pendingSubmitQuery ||
      this.pendingSubmitQuery !== query ||
      this.searchTerm !== query ||
      !this.hasCurrentResults() ||
      this.hasAttribute("loading")
    ) {
      return;
    }

    const hasNoResults = this.querySelector(
      ".predictive-search__empty-message",
    );

    this.pendingSubmitQuery = "";
    if (hasNoResults) return;

    setTimeout(() => {
      if (
        this.getQuery() === query &&
        this.hasCurrentResults() &&
        !this.hasAttribute("loading")
      ) {
        this.submitSearch();
      }
    });
  }

  scopeResultIds() {
    const instanceIndex = Array.from(
      document.querySelectorAll("predictive-search"),
    ).indexOf(this);
    const idSuffix = `-${instanceIndex + 1}`;
    const idMap = new Map();

    this.predictiveSearchResults
      .querySelectorAll('[id^="predictive-search-"]')
      .forEach((element) => {
        const currentId = element.id;
        const scopedId = `${currentId}${idSuffix}`;
        idMap.set(currentId, scopedId);
        element.id = scopedId;
      });

    const listbox = this.predictiveSearchResults.querySelector('[role="listbox"]');
    if (listbox?.querySelector('[role="option"]')) {
      this.input.setAttribute('aria-controls', listbox.id);
    } else {
      listbox?.removeAttribute('role');
      listbox?.removeAttribute('aria-label');
      this.input.removeAttribute('aria-controls');
    }

    [
      "aria-labelledby",
      "aria-describedby",
      "aria-controls",
    ].forEach((attribute) => {
      this.querySelectorAll(`[${attribute}]`).forEach((element) => {
        const references =
          element.getAttribute(attribute)?.split(/\s+/).filter(Boolean) || [];
        const scopedReferences = references.map(
          (reference) => idMap.get(reference) || reference,
        );
        element.setAttribute(attribute, scopedReferences.join(" "));
      });
    });
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent,
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector(".section-header")?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.isAgHeaderSearch
      ? "none"
      : this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", Boolean(this.predictiveSearchResults.querySelector('[role="listbox"]')));
    this.isOpen = true;
  }

  limitHeaderPredictiveResults() {
    const resultsRoot = this.querySelector("#predictive-search-results");
    if (!resultsRoot) return;

    if (resultsRoot.querySelector("[data-ag-header-results-prepared]")) return;

    const articleListSelector = this.isAgMobileHeaderSearch
      ? "#predictive-search-results-pages-list-mobile"
      : "#predictive-search-results-pages-list-desktop";
    const productItems = Array.from(
      resultsRoot.querySelectorAll(
        '#predictive-search-results-products-list [data-predictive-search-resource="product"]',
      ),
    );
    const articleItems = Array.from(
      resultsRoot.querySelectorAll(
        `${articleListSelector} [data-predictive-search-resource="article"]`,
      ),
    );
    const pageItems = Array.from(
      resultsRoot.querySelectorAll(
        `${articleListSelector} [data-predictive-search-resource="page"]`,
      ),
    );
    const maxResults = 3;
    const contentItems = [...articleItems, ...pageItems];
    const productLimit = contentItems.length > 0 ? maxResults - 1 : maxResults;
    const orderedItems = productItems.slice(0, productLimit);

    orderedItems.push(
      ...contentItems.slice(0, maxResults - orderedItems.length),
    );

    const itemsToKeep = new Set(orderedItems);
    const targetList =
      resultsRoot.querySelector("#predictive-search-results-products-list") ||
      resultsRoot.querySelector(articleListSelector);

    orderedItems.forEach((item) => targetList?.appendChild(item));

    resultsRoot
      .querySelectorAll(".predictive-search__list-item")
      .forEach((item) => {
        if (!itemsToKeep.has(item)) {
          item.remove();
        }
      });

    resultsRoot
      .querySelectorAll(".predictive-search__result-group > div")
      .forEach((section) => {
        if (!section.querySelector(".predictive-search__list-item")) {
          section.remove();
        }
      });

    resultsRoot
      .querySelectorAll(
        ".predictive-search__pages-wrapper, .predictive-search__result-group",
      )
      .forEach((group) => {
        if (!group.querySelector(".predictive-search__list-item")) {
          group.remove();
        }
      });

    const groupsWrapper = resultsRoot.querySelector(
      "#predictive-search-results-groups-wrapper",
    );
    if (
      groupsWrapper &&
      !groupsWrapper.querySelector(".predictive-search__list-item")
    ) {
      groupsWrapper.remove();
    }
  }

  close(clearSearchTerm = false) {
    this.pendingSubmitQuery = "";
    this.abortPendingRequests();
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.searchTerm = "";
      this.resultsSearchTerm = "";
      this.removeAttribute("results");
    }
    this.clearSelectedOption();
    if (this.statusElement) this.statusElement.textContent = "";
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");
  }

  clearSelectedOption() {
    this.querySelector('[aria-selected="true"]')?.setAttribute("aria-selected", false);
    this.input.removeAttribute("aria-activedescendant");
  }
}

customElements.define("predictive-search", PredictiveSearch);
